import { Container, Sprite, type Texture } from 'pixi.js';
import type { CollectState } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { findLine, GOLD_LINE, GOLD_TITLE, prizeText, relicsFor, SET_TITLE, type Relic } from '../../logic/collect/relics';
import { Confetti } from '../../widgets/board/Confetti';
import type { TitleCardOptions } from '../../widgets/board/TitleCard';
import { animate, ease, prefersReducedMotion } from '../../widgets/board/ui';
import { RelicShelf, type RelicShelfArt, type RelicShelfMode } from '../../widgets/collect/RelicShelf';
import { RelicsBoard } from '../../widgets/collect/RelicsBoard';
import type { Find } from '../../widgets/slot/FindReveal';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

/** What the host shows: the slot and its relics, or null for none. */
export interface RelicsView {
  themeId: string;
  state: CollectState;
}

/** Where things are, from the scene. */
export interface RelicsDeps extends RelicShelfArt {
  coin: () => Texture | null;
  gem: () => Texture | null;
  /** The action row's free stretch between BET and SPIN. */
  actionGap: () => { left: number; right: number; y: number; height: number } | null;
  /** Show a find on the slot's find board; resolves when it has gone. */
  showFind: (find: Find) => Promise<void>;
  /** Show the relics board over the slot; resolves when it has closed. */
  showBoard: (board: RelicsBoard, play: (board: RelicsBoard) => Promise<void>) => Promise<void>;
  /** The TV title card. */
  titleCard: (text: string, options: TitleCardOptions) => Promise<void>;
  /** Coins fly from `from` into the coin counter, crediting as they land. */
  flyCoins: (from: { x: number; y: number }, amount: number) => Promise<void>;
  /** Gems fly from `from` to the gem counter and are credited. */
  flyGems: (from: { x: number; y: number }, amount: number) => Promise<void>;
  /** Whether the board may open now (the reels are still). */
  canOpen: () => boolean;
}

/** Sounds for the moments (loaded with every slot). */
const FIND_STING = 'bonus:relic_found';
const SET_STING = 'bonus:relic_set';
/** The gold mastery card's fanfare (the slot's own jackpot sting). */
const GOLD_STING = 'win:sting_jackpot';

/**
 * Reel collectibles on the slot (`reel_collect`): the relic shelf on the
 * action row, the relic flying from its reel to the shelf, the find board
 * for the first and second, and for the third the title card, the prize
 * flying to the wallet and confetti. The relic a slot gives for reaching
 * gold opens with its own title card first. A tap on the shelf opens the
 * relics board. The server has decided and paid everything; this shows it.
 */
export class RelicsComponent extends Container implements SceneComponent {
  /** The flying relic and the confetti, kept above the slot by the scene. */
  readonly overlay = new Container();
  private readonly shelf: RelicShelf;
  private readonly board: RelicsBoard;
  private readonly confetti = new Confetti();
  private view: RelicsView | null = null;
  private relics: Relic[] = [];
  private ctx: SceneLayoutContext | null = null;
  private busy = false;
  /** What the relics are doing now, for the browser tests. */
  private moment: 'idle' | 'gold' | 'flying' | 'find' | 'set' = 'idle';

  constructor(private readonly deps: RelicsDeps) {
    super();
    this.shelf = new RelicShelf(deps, []);
    this.shelf.onTap = () => void this.openBoard();
    this.board = new RelicsBoard(deps);
    this.addChild(this.shelf);
    this.overlay.addChild(this.confetti);
    this.visible = false;
  }

  /** The shelf and board as plain data, for the browser tests. */
  get snapshot(): {
    shelf: RelicShelf['snapshot'];
    board: RelicsBoard['snapshot'];
    state: CollectState | null;
    moment: 'idle' | 'gold' | 'flying' | 'find' | 'set';
  } {
    return { shelf: this.shelf.snapshot, board: this.board.snapshot, state: this.view?.state ?? null, moment: this.moment };
  }

  /** True while a relic moment or the board is on screen. */
  get blocking(): boolean {
    return this.busy || this.board.visible;
  }

  /** The slot's relics, or null to hide the shelf. */
  setView(view: RelicsView | null): void {
    const themeChanged = view?.themeId !== this.view?.themeId;
    this.view = view;
    if (view && themeChanged) {
      this.relics = relicsFor(view.themeId);
      this.shelf.setRelics(this.relics);
    }
    this.visible = view !== null && this.relics.length > 0;
    if (view && !this.busy) {
      this.shelf.setHeld(view.state.pieces);
      this.shelf.setPrize(view.state.prize);
    }
    if (this.ctx) this.layout(this.ctx);
  }

  /** The relics board, from a tap on the shelf. */
  async openBoard(): Promise<void> {
    if (!this.view || this.busy || this.board.visible || !this.deps.canOpen()) return;
    const state = this.view.state;
    await this.deps.showBoard(this.board, (b) => b.play(this.relics, state));
  }

  /** Press a button on the relics board by its label (browser tests). */
  press(label: string): boolean {
    return this.board.press(label);
  }

  /** The texture a found relic lands as on the reels. */
  relicArt(piece: number): string | null {
    return this.relics[piece]?.art ?? null;
  }

  /** The reel symbol a found relic lands as. */
  relicAt(piece: number): Relic | null {
    return this.relics[piece] ?? null;
  }

  /**
   * A spin's relics, once its reels have landed: for gold mastery's relic
   * its title card first; then the relic flies from `from` (canvas pixels,
   * the reel cell) to its socket, then the find board, or for the third the
   * set's moment. Resolves when it's over.
   */
  async play(state: CollectState, from: { x: number; y: number } | null): Promise<void> {
    const find = state.find;
    if (!find || !this.view) {
      if (this.view) this.setView({ ...this.view, state });
      return;
    }
    this.busy = true;
    try {
      const relic = this.relics[find.piece];
      if (find.gold === true) {
        this.moment = 'gold';
        await this.deps.titleCard(GOLD_TITLE, { sub: GOLD_LINE, sting: GOLD_STING, kind: 'complete' });
      }
      this.moment = 'flying';
      if (relic && from) await this.fly(relic, from, find.piece);
      await this.shelf.light(find.piece);
      const completed = state.completed;
      if (!completed) {
        this.view = { ...this.view, state };
        this.shelf.setHeld(state.pieces);
        this.moment = 'find';
        if (relic) {
          await this.deps.showFind({
            name: relic.name,
            glyph: relic.glyph,
            rarity: relic.rarity,
            collected: state.pieces.filter(Boolean).length,
            total: state.pieces.length,
            art: this.deps.relic(relic.art),
            line: findLine(state),
          });
        }
        return;
      }
      // The third: all three glow, the title card, the prize, confetti.
      this.moment = 'set';
      await this.shelf.flashAll();
      const prize = { coins: completed.coins, gems: completed.gems };
      await this.deps.titleCard(SET_TITLE, { sub: prizeText(prize), sting: SET_STING, kind: 'complete' });
      const at = this.shelf.centre;
      void this.confetti.burst(at.x, at.y - 30, { count: 110, spread: 360 });
      if (completed.coins > 0) await this.deps.flyCoins(at, completed.coins);
      if (completed.gems > 0) await this.deps.flyGems(at, completed.gems);
      await this.shelf.empty();
      this.view = { ...this.view, state };
      this.shelf.setHeld(state.pieces);
      this.shelf.setPrize(state.prize);
    } finally {
      this.busy = false;
      this.moment = 'idle';
    }
  }

  layout(ctx: SceneLayoutContext): void {
    this.ctx = ctx;
    this.board.layout(ctx.width, ctx.height);
    // Beside the reels where there's room (wide screens): the three relics
    // one above another, with the prize.
    const right = ctx.machineRight ?? ctx.width;
    const machineH = ctx.machineBottom - ctx.machineTop;
    const sideSize = Math.min(58, Math.floor((machineH - 52) / 3.3));
    if (sideSize >= 40 && ctx.width - right - 16 >= RelicShelf.widthFor('side', sideSize) + 24) {
      const w = RelicShelf.widthFor('side', sideSize);
      this.shelf.layout('side', right + 24 + w / 2, (ctx.machineTop + ctx.machineBottom) / 2, sideSize);
      return;
    }
    // Otherwise on the action row, between BET and SPIN.
    const gap = this.deps.actionGap();
    if (!gap) return;
    const room = gap.right - gap.left - 12;
    const rowSize = Math.min(48, Math.max(34, Math.round(gap.height * 0.95)));
    let mode: RelicShelfMode = 'row';
    let size = rowSize;
    if (RelicShelf.widthFor('row', size) > room) {
      // Smaller bezels before folding, down to 34px.
      size = Math.floor((room - 16) / (3 + 0.36));
      if (size < 34) {
        mode = 'compact';
        size = Math.max(30, Math.min(rowSize, room - 12));
      }
    }
    const x = gap.left + 6 + room / 2;
    this.shelf.layout(mode, x, gap.y, size);
  }

  destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.overlay.destroy({ children: true });
    this.board.destroy({ children: true });
    super.destroy(options);
  }

  /** The relic arcs from its reel cell to its socket on the shelf. */
  private async fly(relic: Relic, from: { x: number; y: number }, piece: number): Promise<void> {
    const texture = this.deps.relic(relic.art);
    if (!texture || prefersReducedMotion()) return;
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    this.overlay.addChild(sprite);
    const to = this.shelf.socketCentre(piece);
    const start = 96 / Math.max(1, texture.width, texture.height);
    const end = (this.shelf.bezelSize * 0.74) / Math.max(1, texture.width, texture.height);
    AudioBus.shared.play(FIND_STING);
    // Lift a little first, then arc down into the socket.
    const lift = Math.min(160, Math.abs(to.y - from.y) * 0.5 + 60);
    await animate(720, (t) => {
      const k = t < 0.5 ? ease.inCubic(t * 2) / 2 : 0.5 + ease.outCubic(t * 2 - 1) / 2;
      sprite.x = from.x + (to.x - from.x) * k;
      sprite.y = from.y + (to.y - from.y) * k - lift * Math.sin(Math.PI * t);
      sprite.scale.set(start + (end - start) * k);
      sprite.rotation = 0.35 * Math.sin(Math.PI * t);
    });
    sprite.destroy();
  }
}
